<script setup lang="ts">
/**
 * Shell for the public landing page.
 *
 * Separate from `AuthLayout` on purpose: that one wraps /login, /register and
 * the password flows, where a marketing nav and a language switcher would be
 * noise around a form. This one is the front door — it carries the product
 * nav, the sign-in pair, and the full footer.
 *
 * The language switcher is real, not decoration. A visitor deciding whether to
 * sign up has no account yet, so there is no stored preference to read: the
 * page opens in whatever `browserLanguage()` resolved to at boot, and this is
 * the only way for them to say otherwise before they have an account.
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { SUPPORTED_LOCALES, chooseLocale, locale, useI18n } from '../i18n/index.ts';

const { t } = useI18n();
const year = new Date().getFullYear();

/** Anchors within the page, so they work from the landing itself. */
const sections = computed(() => [
  { href: '#features', label: t('landing.nav.features') },
  { href: '#how', label: t('landing.nav.how') },
  { href: '#interests', label: t('landing.nav.interests') },
]);

const LOCALE_LABELS: Record<string, string> = { th: 'ไทย', en: 'EN' };
</script>

<template>
  <div class="flex min-h-full flex-col bg-surface text-body-md text-on-surface">
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white"
    >
      {{ t('nav.skipToContent') }}
    </a>

    <header
      class="tv-glass sticky top-0 z-50 border-x-0 border-t-0"
      :style="{ height: 'var(--header-height)' }"
    >
      <div class="mx-auto flex h-full max-w-7xl items-center justify-between gap-space-md px-4 sm:px-6 lg:px-12">
        <RouterLink to="/" class="flex shrink-0 items-center gap-space-sm">
          <img src="/logo.png" alt="" class="h-8 w-8" />
          <span class="text-headline-sm font-bold tracking-tight">TalkVibe</span>
        </RouterLink>

        <nav class="hidden items-center gap-space-md md:flex" :aria-label="t('landing.nav.label')">
          <a
            v-for="section in sections"
            :key="section.href"
            :href="section.href"
            class="rounded-full px-space-sm py-space-xs text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
          >
            {{ section.label }}
          </a>
        </nav>

        <div class="flex items-center gap-space-sm">
          <div class="flex items-center rounded-full bg-surface-container-low p-space-xs">
            <button
              v-for="code in SUPPORTED_LOCALES"
              :key="code"
              type="button"
              class="rounded-full px-space-sm py-0.5 text-label-sm transition-colors"
              :class="
                locale === code
                  ? 'bg-surface-container-lowest text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              "
              :aria-pressed="locale === code"
              @click="chooseLocale(code)"
            >
              {{ LOCALE_LABELS[code] ?? code.toUpperCase() }}
            </button>
          </div>

          <RouterLink
            to="/login"
            class="hidden rounded-full px-space-md py-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface sm:inline-flex"
          >
            {{ t('auth.signIn') }}
          </RouterLink>
          <RouterLink
            to="/register"
            class="inline-flex items-center justify-center rounded-full bg-brand-600 px-space-lg py-2 text-label-md text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            {{ t('auth.signUp') }}
          </RouterLink>
        </div>
      </div>
    </header>

    <main id="main" class="flex flex-1 flex-col">
      <slot />
    </main>

    <footer class="bg-surface-container-low">
      <div
        class="mx-auto flex max-w-7xl flex-col items-center justify-between gap-space-md px-4 py-space-xl sm:px-6 md:flex-row lg:px-12"
      >
        <p class="text-label-sm text-on-surface-variant">
          © {{ year }} TalkVibe. {{ t('landing.footer.rights') }}
        </p>
        <nav class="flex flex-wrap items-center justify-center gap-space-md" :aria-label="t('landing.footer.label')">
          <!--
            Before the legal links, because it is the one thing on this footer
            somebody might actually be looking for. The desktop app is the only
            way to be told about a message without a tab open, which is the
            reason to want it.
          -->
          <RouterLink
            to="/download"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-on-surface"
          >
            {{ t('download.footerLink') }}
          </RouterLink>
          <RouterLink
            to="/terms"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-on-surface"
          >
            {{ t('legal.terms') }}
          </RouterLink>
          <RouterLink
            to="/privacy"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-on-surface"
          >
            {{ t('legal.privacy') }}
          </RouterLink>
          <RouterLink
            to="/login"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-on-surface"
          >
            {{ t('auth.signIn') }}
          </RouterLink>
        </nav>
      </div>
    </footer>
  </div>
</template>
